import { agencyStrings } from '@data/agency';
import { vesselScheduleStrings, vesselScheduleWritePermissions } from '@data/vesselSchedule';
import { Agency } from '@models/Agency';
import { PaginationType } from '@models/next/PaginationType';
import { User } from '@models/User';
import { makeMessagesEitherLeft, makeMessagesEitherRight, MessagesEither } from '@utils/errors';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export const VesselScheduleFields = z.object({
  id: z.number(),
  vessel: z.string().label(vesselScheduleStrings.vessel),
  callsign: z.string().label(vesselScheduleStrings.callsign),
  voyage: z.string().label(vesselScheduleStrings.voyage),
  pol: z.string().label(vesselScheduleStrings.pol),
  pod: z.string().label(vesselScheduleStrings.pod),
  etd: f.dateString().label(vesselScheduleStrings.etd),
  eta: f.dateString().label(vesselScheduleStrings.eta),
  atd: f.dateString().nullish().label(vesselScheduleStrings.atd),
  ata: f.dateString().nullish().label(vesselScheduleStrings.ata),
  agency_id: z.number().nullish(),
});

export const VesselScheduleItem = VesselScheduleFields;

export type VesselScheduleItem = z.infer<typeof VesselScheduleItem>;

export const VesselScheduleSearchQuery = z
  .object({
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    vessel: VesselScheduleFields.shape.vessel,
    voyage: VesselScheduleFields.shape.voyage,
    pol: VesselScheduleFields.shape.pol,
    pod: VesselScheduleFields.shape.pod,
    etd_after: VesselScheduleFields.shape.etd,
    eta_after: VesselScheduleFields.shape.eta,
    etd_before: VesselScheduleFields.shape.etd,
    eta_before: VesselScheduleFields.shape.eta,
    has_atd: z.boolean(),
  })
  .partial();

export type VesselScheduleSearchQuery = z.infer<typeof VesselScheduleSearchQuery>;

export const VesselSchedulePages = z.object({
  count: z.number(),
  results: z.array(VesselScheduleItem),
});

export type VesselSchedulePages = z.infer<typeof VesselSchedulePages>;

export const VesselScheduleItemForm = z.object({
  vessel: VesselScheduleFields.shape.vessel.min(1),
  callsign: VesselScheduleFields.shape.callsign.min(1),
  voyage: VesselScheduleFields.shape.voyage.min(1),
  pol: VesselScheduleFields.shape.pol.min(1),
  pod: VesselScheduleFields.shape.pod.min(1),
  etd: VesselScheduleFields.shape.etd,
  eta: VesselScheduleFields.shape.eta,
  atd: VesselScheduleFields.shape.atd,
  ata: VesselScheduleFields.shape.ata,
});

export type VesselScheduleItemForm = z.infer<typeof VesselScheduleItemForm>;

export const VesselScheduleItemsForm = VesselScheduleItemForm.array();

export type VesselScheduleItemsForm = z.infer<typeof VesselScheduleItemsForm>;

export const ErrorVesselScheduleLockedD2dNos = z.string().array();

export class VesselScheduleHelper {
  static canWriteVesselSchedule(
    vesselSchedule: VesselScheduleItem,
    user: User,
    agency?: Agency,
  ): MessagesEither {
    if (user.role === 'admin' || user.role === 'FCJapan') {
      return makeMessagesEitherRight();
    }

    // FCChina
    if (!agency) {
      return makeMessagesEitherLeft({
        messages: [agencyStrings.errorNoAgency!],
      });
    }

    if (vesselSchedule.agency_id === user.agency_id) {
      return makeMessagesEitherRight();
    }

    const writePermissions = vesselScheduleWritePermissions[agency.fcp_no];

    if (!writePermissions || writePermissions.length === 0) {
      return makeMessagesEitherLeft({
        messages: [vesselScheduleStrings.cannotUpdateAnyVesselForAgency],
      });
    }

    if (!writePermissions.includes(vesselSchedule.pol)) {
      return makeMessagesEitherLeft({
        messages: [vesselScheduleStrings.cannotUpdateVesselForAgency(writePermissions)],
      });
    }

    return makeMessagesEitherRight();
  }
}
